import { Search, Star, ArrowUpDown } from "lucide-react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "../../components/ui/avatar";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { useState } from "react";


// Helper function to format relative time
const getRelativeTime = (date) => {
  if (!date) return "Never";
  
  const now = new Date();
  const past = new Date(date);
  const diffInMs = now - past;
  const diffInSeconds = Math.floor(diffInMs / 1000);
  const diffInMinutes = Math.floor(diffInSeconds / 60);
  const diffInHours = Math.floor(diffInMinutes / 60);
  const diffInDays = Math.floor(diffInHours / 24);
  const diffInMonths = Math.floor(diffInDays / 30);
  const diffInYears = Math.floor(diffInDays / 365);

  if (diffInSeconds < 60) return "Just now";
  if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
  if (diffInHours < 24) return `${diffInHours}h ago`;
  if (diffInDays < 30) return `${diffInDays}d ago`;
  if (diffInMonths < 12) return `${diffInMonths}mo ago`;
  return `${diffInYears}y ago`;
};

// Helper function to format exact date
const getExactDate = (date) => {
  if (!date) return "Never";
  return new Date(date).toLocaleString();
};

export const UserList = ({
  searchTerm,
  setSearchTerm,
  statusFilter,
  setStatusFilter,
  filteredUsers,
  setSelectedUser,
  userListings,
}) => {
  const [sortBy, setSortBy] = useState("joinDate-desc");

  // Sort users based on selected criteria
  const sortedUsers = [...filteredUsers].sort((a, b) => {
    const [field, direction] = sortBy.split("-");
    const isAsc = direction === "asc";
    
    let aValue, bValue;
    
    switch (field) {
      case "joinDate":
        aValue = new Date(a.createdAt);
        bValue = new Date(b.createdAt);
        break;
      case "lastActive":
        aValue = a.lastActiveAt ? new Date(a.lastActiveAt) : new Date(0);
        bValue = b.lastActiveAt ? new Date(b.lastActiveAt) : new Date(0);
        break;
      case "rating":
        aValue = a.rating || 0;
        bValue = b.rating || 0;
        break;
      case "exchanges":
        aValue = a.exchangeCount || 0;
        bValue = b.exchangeCount || 0;
        break;
      case "reports":
        aValue = a.reportCount || 0;
        bValue = b.reportCount || 0;
        break;
      default:
        return 0;
    }
    
    if (aValue < bValue) return isAsc ? -1 : 1;
    if (aValue > bValue) return isAsc ? 1 : -1;
    return 0;
  });

  // Filter users based on status (active = not banned, banned = isBanned true)
  const statusFilteredUsers = sortedUsers.filter((user) => {
    const matchesSearch =
      user.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.lastName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase());
    
    let matchesStatus = true;
    if (statusFilter === "active") {
      matchesStatus = !user.isBanned;
    } else if (statusFilter === "banned") {
      matchesStatus = user.isBanned;
    }
    
    return matchesSearch && matchesStatus;
  });

  return (
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>User Management</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col sm:flex-row gap-4 mb-6">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <Input
                  placeholder="Search users by name or email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-48">
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="banned">Banned</SelectItem>
                </SelectContent>
              </Select>

              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="w-56">
                  <ArrowUpDown className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Sort by" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="joinDate-desc">Join Date ↓ (Newest)</SelectItem>
                  <SelectItem value="joinDate-asc">Join Date ↑ (Oldest)</SelectItem>
                  <SelectItem value="lastActive-desc">Last Active ↓ (Recent)</SelectItem>
                  <SelectItem value="lastActive-asc">Last Active ↑ (Oldest)</SelectItem>
                  <SelectItem value="rating-desc">Rating ↓ (Highest)</SelectItem>
                  <SelectItem value="rating-asc">Rating ↑ (Lowest)</SelectItem>
                  <SelectItem value="exchanges-desc">Exchanges ↓ (Most)</SelectItem>
                  <SelectItem value="exchanges-asc">Exchanges ↑ (Least)</SelectItem>
                  <SelectItem value="reports-desc">Reports ↓ (Most)</SelectItem>
                  <SelectItem value="reports-asc">Reports ↑ (Least)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>User</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Join Date</TableHead>
                    <TableHead>Last Active</TableHead>
                    <TableHead>Rating</TableHead>
                    <TableHead>Exchanges</TableHead>
                    <TableHead>Reports/Warnings</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {statusFilteredUsers.map((user) => (
                    <TableRow
                      key={user._id}
                      className="cursor-pointer hover:bg-gray-50"
                    >
                      <TableCell>
                        <div className="flex items-center space-x-3">
                          <Avatar className="w-10 h-10">
                            <AvatarImage src={user.image} />
                            <AvatarFallback>
                              {(user.firstName?.[0] || "") + (user.lastName?.[0] || "")}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <div className="font-medium">
                              {user.firstName} {user.lastName}
                            </div>
                            <div className="text-sm text-gray-500">
                              {user.email}
                            </div>
                          </div>
                        </div>
                      </TableCell>
                      
                      <TableCell>
                        <Badge
                          variant={user.isBanned ? "destructive" : "default"}
                          className={user.isBanned ? "bg-red-100 text-red-800" : "bg-green-100 text-green-800"}
                        >
                          {user.isBanned ? "Banned" : "Active"}
                        </Badge>
                      </TableCell>
                      
                      <TableCell>
                        {getExactDate(user.createdAt)}
                      </TableCell>
                      
                      <TableCell>
                        {getRelativeTime(user.lastActiveAt)}
                      </TableCell>
                      
                      <TableCell>
                        <div className="flex items-center space-x-1">
                          <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                          <span>{user.rating || 0}</span>
                        </div>
                      </TableCell>
                      
                      <TableCell>
                        <span className="font-medium">{user.exchangeCount || 0}</span>
                      </TableCell>
                      
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          <Badge 
                            variant="outline" 
                            className={`text-xs ${
                              (user.reportCount || 0) > 0 
                                ? "bg-red-50 text-red-700 border-red-200" 
                                : "bg-gray-50 text-gray-600 border-gray-200"
                            }`}
                          >
                            Reports: {user.reportCount || 0}
                          </Badge>
                          <Badge 
                            variant="outline" 
                            className={`text-xs ${
                              (user.warningCount || 0) > 0 
                                ? "bg-orange-50 text-orange-700 border-orange-200" 
                                : "bg-gray-50 text-gray-600 border-gray-200"
                            }`}
                          >
                            Warnings: {user.warningCount || 0}
                          </Badge>
                        </div>
                      </TableCell>
                      
                      <TableCell>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setSelectedUser(user)}
                        >
                          View Details
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            
            {statusFilteredUsers.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                No users found matching your criteria.
              </div>
            )}
          </CardContent>
        </Card>
      </div>
  );
};